<template>
  <div
    @click.prevent.stop="openReport"
    title="Print Report"
    v-bind="$attrs">
    <reports-icon class="h-6" />
  </div>
  <teleport to="body">
    <transition
      name="fade"
      v-if="preparingReport">
      <div class="p-10 fixed inset-0 bg-black bg-opacity-80 flex flex-row justify-center items-center z-40">
        <close-button @clicked="closeReport()" />
        <spinner-message
          v-show="pdfDownloading"
          message="It can take a couple of seconds for the PDF to generate.">
          <button @click.prevent="pdfDownloading = false">
            Please Click here after the PDF has downloaded, if this message doesn't close on it's own.
          </button>
        </spinner-message>
        <div class="absolute top-12 left-12 right-12 bottom-12 bg-white overflow-auto">
          <a
            class="absolute top-10 right-10 inline-block btn btn-primary mb-4"
            :href="`/report-download/${scoUserId}`"
            @click="initiatePdfDownload"
            download="download"
            >Download PDF</a
          >
          <spinner-message v-if="!loaded" />
          <div
            v-else
            class="mx-auto py-10 report-width">
            <opus-logo class="w-1/2" />

            <h1 class="my-8">{{ courseTitle }}</h1>

            <p>Name of learner: {{ learnerName }}</p>
            <p>Assessment date: {{ formatDate(assessmentDate) }}</p>
            <p>Attempt: {{ attempt }} of 2</p>
            <p>Time taken: {{ timeTaken }}</p>

            <h2 class="my-4">Results</h2>

            <p>
              You are {{ Math.round(totalPercentageScore) }}% competent in the '{{ courseTitle }}'. Download this
              document and keep it in a safe place to prepare for your next assessment.
            </p>

            <div v-if="fullyCompetent.length > 0">
              <h2 class="mt-4 mb-2">You can competently perform these tasks:</h2>
              <table class="w-full">
                <thead>
                  <tr class="bg-gray-300">
                    <th class="border border-gray-800 px-2 py-1 text-left">Competency</th>
                    <th class="border border-gray-800 px-2 py-1">Questions asked</th>
                    <th class="border border-gray-800 px-2 py-1">Correct answers</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(competency, idx) in fullyCompetent"
                    :key="idx">
                    <td class="border border-gray-800 px-2 py-1">{{ competency.title }}</td>
                    <td class="border border-gray-800 px-2 py-1 text-center">{{ competency.question_count }}</td>
                    <td class="border border-gray-800 px-2 py-1 text-center">{{ competency.correct_count }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="incompetent.length > 0">
              <h2 class="mt-4 mb-2">You need to develop further in these areas:</h2>
              <table class="w-full">
                <thead>
                  <tr class="bg-gray-300">
                    <th class="border border-gray-800 px-2 py-1 text-left">Competency</th>
                    <th class="border border-gray-800 px-2 py-1">Questions asked</th>
                    <th class="border border-gray-800 px-2 py-1">Correct answers</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(competency, idx) in incompetent"
                    :key="idx">
                    <td class="border border-gray-800 px-2 py-1">{{ competency.title }}</td>
                    <td class="border border-gray-800 px-2 py-1 text-center">{{ competency.question_count }}</td>
                    <td class="border border-gray-800 px-2 py-1 text-center">{{ competency.correct_count }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else>
              <h2 class="mt-4 mb-2">You need to develop further in these areas: None.</h2>
            </div>

            <template v-if="incompetent.length > 0">
              <p
                class="my-4"
                v-if="attempt === 1">
                Once you have reviewed your study materials on the above topics, come back to try again. You have one
                more chance to answer the questions you got wrong on your first try.
              </p>
              <p
                class="my-4"
                v-if="attempt > 1">
                Once you have reviewed your study materials on the above topics, ask your manager to supply you with
                another licence to reattempt this assessment.
              </p>
            </template>
            <template v-else>
              <p class="my-4">
                Well done! Store this report in a safe place, as you may be asked to present it as proof of your
                competency.
              </p>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import ReportsIcon from "@/components/icons/ReportsIcon";
import ApiService from "@/services/ApiService";
import CloseButton from "@/components/utilities/CloseButton.vue";
import OpusLogo from "@/components/OpusLogo.vue";
import dayjs from "dayjs";
const utc = require("dayjs/plugin/utc");
dayjs.extend(utc);

export default {
  name: "ScoCompetencyReport",
  components: { ReportsIcon, CloseButton, OpusLogo },
  props: {
    scoUserId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      preparingReport: false,
      showReport: false,
      competencies: [],
      fullyCompetent: [],
      incompetent: [],
      rawScore: null,
      learnerName: "",
      courseTitle: "",
      assessmentDate: "",
      attempt: 1,
      timeTaken: "",
      loaded: false,
      pdfDownloading: false
    };
  },
  computed: {
    totalNumberOfQuestions() {
      let questionCount = 0;

      this.competencies.forEach((c) => {
        questionCount += c.question_count;
      });

      return questionCount;
    },
    totalPercentageScore() {
      return this.rawScore;
    }
  },
  methods: {
    initiatePdfDownload() {
      window.addEventListener("blur", this.hidePdfDownloading, false);
      console.log("start showing pdf generating message");
      this.pdfDownloading = true;
    },
    hidePdfDownloading() {
      window.removeEventListener("blur", this.hidePdfDownloading, false);
      console.log("STOP showing pdf generating message");
      this.pdfDownloading = false;
    },
    formatDate(date) {
      return dayjs(date).utc().format("DD/MM/YYYY");
    },
    openReport() {
      this.preparingReport = true;
      this.fetchResults();
      this.$nextTick(() => {
        this.showReport = true;
      });
    },
    closeReport() {
      console.log("close report clicked");
      this.showReport = false;
      this.$nextTick(() => {
        this.preparingReport = false;
      });
    },
    competencyPercentageScore(c) {
      if (c.correct_count === 0) {
        return 0;
      }

      return Math.ceil((c.correct_count / c.question_count) * 100 * 100) / 100;
    },
    fetchResults() {
      ApiService.fetchScoUserResults(this.scoUserId)
        .then((response) => {
          this.fullyCompetent = [];
          this.incompetent = [];
          this.competencies = response.data.competencies;
          this.competencies.forEach((c) => {
            if (c.correct_count === c.question_count) {
              this.fullyCompetent.push(c);
            } else this.incompetent.push(c);
          });
          this.rawScore = response.data.rawScore;
          this.learnerName = response.data.learnerName;
          this.courseTitle = response.data.courseTitle;
          this.assessmentDate = response.data.assessmentDate;
          this.attempt = response.data.attempt;
          this.timeTaken = response.data.timeTaken;
          this.loaded = true;
        })
        .catch(() => {});
    }
  }
};
</script>
<style scoped>
.report-width {max-width: 190mm;}
</style>